/**
 * A2A Library Service
 * Wraps library operations using A2A client instead of direct Supabase calls
 */

import { A2AClient } from '../client.js';

// Initialize A2A client
const a2aClient = new A2AClient(
  process.env.REACT_APP_A2A_BACKEND_URL || process.env.REACT_APP_API_URL,
  'myclass-client-library',
  {
    secretKey: process.env.REACT_APP_A2A_SECRET_KEY,
    enableLogging: process.env.NODE_ENV === 'development'
  }
);

/**
 * A2A Library Service
 * All methods return promises that resolve to A2A response format
 */
export const a2aLibraryService = {
  /**
   * Get list of books
   */
  async getBooks(params) {
    const { tenant_id, category, available, page = 1, limit = 50 } = params;
    
    const result = await a2aClient.executeTask('list_library_books', {
      tenant_id,
      category,
      available,
      page,
      limit
    });

    return {
      success: result.success,
      data: result.books || [],
      pagination: result.pagination || { page, limit, total: 0 }
    };
  },

  /**
   * Get book by ID
   */
  async getBookById(bookId, tenantId) {
    const result = await a2aClient.executeTask('get_book', {
      tenant_id: tenantId,
      book_id: bookId
    });

    return {
      success: result.success,
      data: result.book
    };
  },

  /**
   * Search books
   */
  async searchBooks(query, tenantId) {
    const result = await a2aClient.executeTask('search_books', {
      tenant_id: tenantId,
      query
    });

    return {
      success: result.success,
      data: result.books || []
    };
  },

  /**
   * Create book
   */
  async createBook(bookData, tenantId) {
    const result = await a2aClient.executeTask('create_book', {
      tenant_id: tenantId,
      book_data: bookData
    });

    return {
      success: result.success,
      data: result.book
    };
  },

  /**
   * Update book
   */
  async updateBook(bookId, tenantId, bookData) {
    const result = await a2aClient.executeTask('update_book', {
      tenant_id: tenantId,
      book_id: bookId,
      book_data: bookData
    });

    return {
      success: result.success,
      data: result.book
    };
  },

  /**
   * Delete book
   */
  async deleteBook(bookId, tenantId) {
    const result = await a2aClient.executeTask('delete_book', {
      tenant_id: tenantId,
      book_id: bookId
    });

    return {
      success: result.success
    };
  },

  /**
   * Borrow book
   */
  async borrowBook(bookId, readerId, tenantId, dueDate) {
    const result = await a2aClient.executeTask('borrow_book', {
      tenant_id: tenantId,
      book_id: bookId,
      reader_id: readerId,
      due_date: dueDate
    });

    return {
      success: result.success,
      data: result.borrowing
    };
  },

  /**
   * Return book
   */
  async returnBook(borrowingId, tenantId, returnDate) {
    const result = await a2aClient.executeTask('return_book', {
      tenant_id: tenantId,
      borrowing_id: borrowingId,
      return_date: returnDate
    });

    return {
      success: result.success,
      data: result.borrowing
    };
  },

  /**
   * Renew book
   */
  async renewBook(borrowingId, tenantId, newDueDate) {
    const result = await a2aClient.executeTask('renew_book', {
      tenant_id: tenantId,
      borrowing_id: borrowingId,
      new_due_date: newDueDate
    });

    return {
      success: result.success,
      data: result.borrowing
    };
  },

  /**
   * Get borrowings
   */
  async getBorrowings(params) {
    const { tenantId, readerId, status } = params;
    
    const result = await a2aClient.executeTask('list_borrowings', {
      tenant_id: tenantId,
      reader_id: readerId,
      status
    });

    return {
      success: result.success,
      data: result.borrowings || []
    };
  },

  /**
   * Get overdue books
   */
  async getOverdueBooks(tenantId) {
    const result = await a2aClient.executeTask('get_overdue_books', {
      tenant_id: tenantId
    });

    return {
      success: result.success,
      data: result.overdueBooks || []
    };
  },

  /**
   * Get reader borrowing history
   */
  async getReaderBorrowingHistory(readerId, tenantId) {
    const result = await a2aClient.executeTask('get_reader_borrowing_history', {
      tenant_id: tenantId,
      reader_id: readerId
    });

    return {
      success: result.success,
      data: result.history || []
    };
  },

  /**
   * Get books by category
   */
  async getBooksByCategory(category, tenantId) {
    const result = await a2aClient.executeTask('list_library_books', {
      tenant_id: tenantId,
      category
    });

    return {
      success: result.success,
      data: result.books || []
    };
  }
};

export default a2aLibraryService;

